import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux'; // Importa useSelector
import clsx from 'clsx';

//components
import LoaderContent from '../../../design-system/components/Loader/LoaderContent';
import FileDropZone from '../../../design-system/components/Form/FileDropZone/FileDropZone';

//ui
import { Box, Button, Dialog, DialogTitle, Icon, IconButton, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

//sdk
import { GlobalService, RecordDocumentService } from '@sdk-point/talisis';

// Constants
import { REJECTED_STATUS, REVIEW_STATUS, APPROVED_STATUS } from '../../../shared/constants/record-status';

const GlobalServiceSDK = new GlobalService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);
const RecordDocumentServiceSDK = new RecordDocumentService(process.env.REACT_APP_ENV, process.env.REACT_APP_X_API_KEY);

const BYTE_INTO_MEGABYTE = 1000000;

const useStyles = makeStyles((theme) => ({
    generalContainer: {
        marginBottom: "70px"
    },
    filterContainer: {
        padding: '16px 24px',
    },
    filterProgramLabelContainer: {
        margin: '12px 0',
    },
    titleDocumentListContainer: {
        padding: '16px 24px 16px 24px',
    },
    uploadIcon: {
        cursor: 'pointer',
        fontSize: '24px',
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid rgba(119, 119, 119, 0.15)`,
        '& .MuiTypography-root': {
            flex: 1,
            textAlign: 'center',
        },
    },
    dialogContainer: {
        background: `${theme.palette.colors.fillStroke[500]} !important`,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    dropZoneContainer: {
        width: "100%",
        height: "307px",
        marginBottom: "64px",
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            height: '256px',
            marginBottom: "16px",
        },
    },
    bodyDialogContainer: {
        width: '506px',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    listItem: {
        borderBottom: `0.5px solid ${theme.palette.colors.fillStroke[200]}`,
    },
    approvedImg: {
        display: 'flex',
        margin: 'auto',
        width: 130,
    },
}));

const COMPROMISE_LETTER = process.env.REACT_APP_ENV === 'dev' ? 124 : 92; // ID dinámico basado en el ambiente
const ACUSE_RECEPCION_DOCUMENTS = process.env.REACT_APP_ENV === 'dev' ? 128 : 93; // ID dinámico basado en el ambiente

const UploadDocumentDialog = ({ open, user, onClose, documentSelected, isAdmin, onSuccess }) => {
    const classes = useStyles();
    const [fileInfo, setFileInfo] = useState({});
    const [isModalLoading, setIsModalLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            setFileInfo({});
            setIsModalLoading(false);
        }
    }, [open]);

    const uploadFile = async (file) => {
        try {
            setIsModalLoading(true);
            const newFile = new File([file], file.name.replace(/[^a-zA-Z0-9-_. áéíóúÁÉÍÓÚñÑ]/g, ' ').trim(), { type: file.type });
            const location = 'record/userDocument';
            const fileResult = await GlobalServiceSDK.uploadFile(newFile, location, 'record-document', documentSelected.document_id);

            setFileInfo({ ...fileResult, size: file.size });
            setIsModalLoading(false);
        } catch (error) {
            console.error(error);
        }
    };

    const deleteFile = async (id) => {
        try {
            await GlobalServiceSDK.deleteFile(id);
        } catch (error) {
            console.error(error);
        }
    };

    const resetUploadedFile = async () => {
        if (fileInfo.id) {
            setIsModalLoading(true);
            await deleteFile(fileInfo.id);
            setFileInfo({});
            setIsModalLoading(false);
        }
    };

    const addDocumentToRecord = async () => {
        try {
            setIsModalLoading(true);
            const fileId = fileInfo.id;
            const doc = documentSelected;

            if (doc.record_status_id === REJECTED_STATUS || !!doc.record_id) {
                const body = {
                    record: {
                        file_id: fileId,
                        list_id: doc.list_id,
                        record_status_id: (isAdmin && (doc.document_id === 92 || doc.document_id === 124)) ? APPROVED_STATUS : REVIEW_STATUS,
                    },
                };

                if (doc.record_status_id === REJECTED_STATUS) {
                    await deleteFile(doc.file_id);
                    body.record.approver_person_id = null;
                    body.record.error_message = null;
                }

                try {
                    const response = await RecordDocumentServiceSDK.updateRecord(doc.record_id, body);
                    console.log('Record updated successfully:', response);
                } catch (error) {
                    console.error('Error updating record:', error); 
                    throw error; 
                }
            
            } else {
                const body = {
                    record: {
                        person_id: user.person_id  || user.id,
                        document_id: doc.document_id,
                        file_id: fileId,
                        list_id: doc.list_id,
                        record_status_id: (isAdmin && (doc.document_id === COMPROMISE_LETTER || doc.document_id === ACUSE_RECEPCION_DOCUMENTS)) ? APPROVED_STATUS : REVIEW_STATUS,
                    },
                };
                await RecordDocumentServiceSDK.createRecord(body);
            }

            setFileInfo({});
            onClose();
            setIsModalLoading(false);

            if (typeof onSuccess === 'function') {
                onSuccess(); // Llama al callback para recargar los documentos
            }
        } catch (error) {
            console.error('Error al subir el documento:', error);
        }
    };

    return (
        <Dialog onClose={onClose} open={open} classes={{ paper: classes.dialogContainer }}>
            <DialogTitle className={classes.dialogTitle}>
                <Typography variant="subtitle1">Subir documento</Typography>
                {/* <IconButton onClick={onClose} className='p-0'>
                    <Icon className='ri-<close-line text-fill-stroke-100' />
                </IconButton> */}
            </DialogTitle>
            <Box p={3} className={classes.bodyDialogContainer}>
                <LoaderContent loading={isModalLoading}>
                    {
                        !fileInfo.id &&
                        <Box>
                            <Box pb="16px">
                                <Typography>{`Únicamente archivos ${!!documentSelected.extensions ? documentSelected.extensions.map(item => item.extension.toUpperCase()).join(", ") : ""} con peso máximo de 2 MB`}</Typography>
                            </Box>
                            <FileDropZone
                                className={classes.dropZoneContainer}
                                progressLabel={50}
                                extensions={documentSelected.extensionDetails || {}}
                                onUploadFile={uploadFile}
                            />
                        </Box>
                    }
                    {
                        !!fileInfo.id &&
                        <Box display="flex" justifyContent="center" flexDirection="column" gridGap={16} mb="8px">
                            <Box display="flex" justifyContent="center">
                                <Icon className='ri-file-text-line text-violet-300' style={{ fontSize: 33 }} />
                            </Box>
                            <Box display="flex" justifyContent="center" alignItems="center" gridGap={8}>
                                <Button color='primary' size='large' className='p-0'>{fileInfo.fileName}</Button>
                                <Typography variant="body1" className={clsx("text-fill-stroke-100")}>{`${(fileInfo.size / BYTE_INTO_MEGABYTE).toFixed(2)} MB`}</Typography>
                            </Box>
                            <Button variant='outlined' color='secondary' onClick={resetUploadedFile}>Volver a subir el archivo</Button>
                        </Box>
                    }
                    <Button
                        variant='contained'
                        color='primary'
                        disabled={!fileInfo.id}
                        fullWidth
                        endIcon={<Icon className='ri-arrow-right-line' />}
                        onClick={addDocumentToRecord}
                    >
                        Enviar documento
                    </Button>
                </LoaderContent>
            </Box>
        </Dialog>
    );
};

export default UploadDocumentDialog;
